import { TranslateService } from '@ngx-translate/core';
import { formatCategoryList } from 'src/app/components/admin/category/category-helpers';
import { entityBasedCommunityLink } from 'src/app/components/community/community.helpers';
import { getStatus } from 'src/app/components/profile/user-profile/view-profile/regular-user-profile/regular-user-profile-helper';
import { MaybeNull } from 'src/app/utils';
import { SessionService } from '../../auth/session.service';
import { getImageUrl, isDateLaterNow } from '../../common/app.helpers';
import AppValues from '../../common/app.values';
import { CardModelType, SocialInitiative } from '../../common/custom.types';
import ProjectLinks from '../../common/project.links';
import { CardInfoType, UserRoles } from '../../components/profile/profile.enum';
import {
  Avatar,
  Badge,
  Button,
  CardModel,
  EntityStatus,
  ItemStatus,
  Link,
  UICard,
} from '../../interfaces/views.interface';
import {
  AccountStatus,
  CategoryBasicView,
  CommentSearchView,
  CommunityBasic,
  CommunityStatus,
  FullUser,
  PostBasic,
  ProjectBasic,
  UserRelationToGroup,
  UserRole,
} from '../../swagger-api';
import { CardType } from './cards.types';
import { emptyCard, unsupportedCardError } from './cards.value';

export class CardBuilder {
  private card: CardModel;
  private data: CardModelType;
  private type: CardType;
  private additionalType = '';
  private status: EntityStatus;
  private translateService: TranslateService;
  private sessionService: SessionService;
  private redirectToLoginIfNoSession: boolean;
  private actionBtnHint: string;

  private buttonHintMapper: { [key in CardType]: string } = {
    community: 'BTN_VISIT_COMMUNITY',
    project: 'BTN_VIEW_PROJECT',
    post: 'BTN_VIEW_POST',
    user: 'BTN_VISIT_USER',
    organization: 'BTN_VISIT_USER',
    patient: 'BTN_VISIT_USER',
    doctor: 'BTN_VISIT_USER',
    med_staff: 'BTN_VISIT_USER',
    researcher: 'BTN_VISIT_USER',
    comment: 'BTN_VISIT_USER',
  };

  private buttonRouterLinkMapper: { [key in CardType]: string } = {
    community: ProjectLinks.LINK_COMMUNITY_VIEW,
    project: ProjectLinks.LINK_PROJECTS_VIEW,
    post: ProjectLinks.LINK_POSTS_VIEW,
    user: ProjectLinks.LINK_PROFILE_VIEW,
    organization: ProjectLinks.LINK_PROFILE_VIEW,
    patient: ProjectLinks.LINK_PROFILE_VIEW,
    doctor: ProjectLinks.LINK_PROFILE_VIEW,
    med_staff: ProjectLinks.LINK_PROFILE_VIEW,
    researcher: ProjectLinks.LINK_PROFILE_VIEW,
    comment: ProjectLinks.LINK_POSTS_VIEW,
  };

  public constructor(
    data: CardModelType,
    type: CardType,
    additionalType: string,
    status: EntityStatus,
    redirectToLoginIfNoSession: boolean,
    translateService: TranslateService,
    sessionService: SessionService,
    actionBtnHint: string,
  ) {
    this.data = data;
    this.type = type;
    this.redirectToLoginIfNoSession = redirectToLoginIfNoSession;
    this.status = status;
    this.additionalType = additionalType;
    this.translateService = translateService;
    this.sessionService = sessionService;
    this.actionBtnHint = actionBtnHint;
  }

  /**
   * @description Sets the card info up step by step with mappers help.
   * @returns {CardModel}
   */
  public create(): CardModel {
    this.reset();
    this.setId();
    this.setHeader();
    this.setImage();
    this.setTitle();
    this.setButtonHint();
    this.setButtonRouterLink();
    this.setDescription();
    this.setDate();
    this.setStatus();
    this.setActionButtonHint();
    this.setActionBtnId();
    this.setActionButtonStatus();
    this.setUrlFragment();
    this.setSearchEntityCreator();
    return this.card;
  }

  public getUserRoles(userRoles: string[]): string {
    return userRoles.map((role) => this.translateService.instant(UserRoles[role] as string) as string).join(`, `);
  }

  private reset(): void {
    this.card = emptyCard();
  }

  private setId(): void {
    this.card.id = this.getCardId();
  }

  private getCardId(): string | never {
    switch (this.type) {
      case CardType.POST: {
        const post: PostBasic = this.data as PostBasic;
        return post.entity_id;
      }

      case CardType.USER:
      case CardType.ORGANIZATION:
      case CardType.PATIENT:
      case CardType.DOCTOR:
      case CardType.MED_STAFF:
      case CardType.RESEARCHER: {
        const fullUser: FullUser = this.data as FullUser;
        return fullUser.user_id;
      }

      case CardType.COMMENT: {
        const comment: CommentSearchView = this.data as CommentSearchView;
        return comment.comment_associated_entity.entity_id;
      }

      default:
        throw new Error(unsupportedCardError(this.type));
    }
  }

  private setStatus(): void {
    this.card.status = this.status;
  }

  private setHeader(): void {
    this.card.header = this.getCardHeader();
  }

  private getCardHeader(): string | never {
    switch (this.type) {
      case CardType.USER: {
        const fullUser: FullUser = this.data as FullUser;
        return this.getUserRoles(fullUser.user_roles) || (this.translateService.instant('USER_TITLE') as string);
      }
      case CardType.ORGANIZATION:
        return this.translateService.instant('USER_ORGANIZATION_TITLE') as string;
      case CardType.PATIENT:
        return this.translateService.instant('USER_PATIENT_TITLE') as string;
      case CardType.DOCTOR:
        return this.translateService.instant('USER_DOCTOR_TITLE') as string;
      case CardType.MED_STAFF:
        return this.translateService.instant('USER_MED_STAFF_TITLE') as string;
      case CardType.RESEARCHER:
        return this.translateService.instant('USER_RESEARCHER_TITLE') as string;
      case CardType.POST:
        return this.translateService.instant('POST_TITLE') as string;
      case CardType.COMMENT: {
        const comment: CommentSearchView = this.data as CommentSearchView;
        return this.translateService.instant('POST_COMMENT_TITLE', {
          postTitle: comment.comment_associated_entity.entity_title,
        }) as string;
      }
      default:
        throw new Error(unsupportedCardError(this.type));
    }
  }

  private setImage(): void {
    this.card.image = this.type === CardType.POST || this.type === CardType.COMMENT ? '' : this.defineImage(this.data);
  }

  private setTitle(): void {
    this.card.title = this.getCardTitle();
  }

  private getCardTitle(): string | never {
    switch (this.type) {
      case CardType.POST: {
        const post: PostBasic = this.data as PostBasic;
        return post.title;
      }
      case CardType.USER:
      case CardType.ORGANIZATION:
      case CardType.PATIENT:
      case CardType.DOCTOR:
      case CardType.MED_STAFF:
      case CardType.RESEARCHER: {
        const fullUser: FullUser = this.data as FullUser;
        return fullUser.displayed_name;
      }
      case CardType.COMMENT: {
        const comment: CommentSearchView = this.data as CommentSearchView;
        return comment.comment_text;
      }

      default:
        throw new Error(unsupportedCardError(this.type));
    }
  }

  private setButtonHint(): void {
    this.card.buttonHint = this.buttonHintMapper[this.type];
  }

  private setButtonRouterLink(): void {
    if (this.sessionService.isSessionActive() || !this.redirectToLoginIfNoSession) {
      this.setButtonRouterLinkUrl(`${this.buttonRouterLinkMapper[this.type]}/${this.card.id}`);
    } else {
      this.setButtonRouterLinkUrl(ProjectLinks.LINK_LOGIN);
      this.setButtonRouterReturnUrl(`${this.buttonRouterLinkMapper[this.type].substring(1)}/${this.card.id}`);
    }
  }

  private setButtonRouterReturnUrl(returnUrl: string): void {
    this.card.buttonRouterParams = { returnUrl };
  }

  private setButtonRouterLinkUrl(url: string): void {
    this.card.buttonRouterLink = url;
  }

  private setDescription(): void {
    this.card.description = this.getCardDescription(this.type);
    this.card.descriptionType = this.getCardDescriptionType();
  }

  private getCardDescription(cardType: CardType): string[] | string | never {
    switch (cardType) {
      case CardType.POST:
        return '';
      case CardType.USER: {
        return this.getUserCategories();
      }

      case CardType.ORGANIZATION:
        return [formatCategoryList(this.organizationCategories())];
      case CardType.PATIENT:
        return [formatCategoryList((this.data as FullUser).patient.diseases || [])];
      case CardType.DOCTOR:
        return [formatCategoryList((this.data as FullUser).doctor.specialty || [])];
      case CardType.MED_STAFF:
        return [formatCategoryList((this.data as FullUser).med_staff.specialty || [])];
      case CardType.RESEARCHER:
        return [formatCategoryList((this.data as FullUser).researcher.specialty || [])];
      case CardType.COMMENT:
        return '';
      default:
        throw new Error(unsupportedCardError(this.type));
    }
  }

  public getUserCategories(): string[] | string {
    const fullUser: FullUser = this.data as FullUser;
    const userRoles = fullUser.user_roles;

    if (userRoles.length < 1) {
      return '';
    }

    let categories: CategoryBasicView[] = [];

    userRoles.forEach((role: UserRole) => {
      switch (role) {
        case UserRole.Organization:
          categories = categories.concat(this.organizationCategories());
          break;
        case UserRole.Patient:
          categories = categories.concat(fullUser.patient.diseases || []);
          break;
        case UserRole.Doctor:
          categories = categories.concat(fullUser.doctor.specialty || []);
          break;
        case UserRole.MedStaff:
          categories = categories.concat(fullUser.med_staff.specialty || []);
          break;
        case UserRole.Researcher:
          categories = categories.concat(fullUser.researcher.specialty || []);
          break;
      }
    });

    if (categories.length < 1) {
      return '';
    }

    return [formatCategoryList(categories)];
  }

  private organizationCategories(): CategoryBasicView[] {
    const organizationSpecialties = (this.data as FullUser).organization.specialty || [];
    const organizationDiseases = (this.data as FullUser).organization.diseases || [];

    return [...organizationSpecialties, ...organizationDiseases];
  }

  private getCardDescriptionType(): CardInfoType | never {
    switch (this.type) {
      case CardType.POST:
        return CardInfoType.string;
      case CardType.USER: {
        const fullUser: FullUser = this.data as FullUser;
        return fullUser.user_roles.length > 1 ? CardInfoType.list : CardInfoType.string;
      }

      case CardType.ORGANIZATION:
      case CardType.PATIENT:
      case CardType.DOCTOR:
      case CardType.MED_STAFF:
      case CardType.RESEARCHER:
        return CardInfoType.list;
      case CardType.COMMENT: {
        return CardInfoType.creatorLink;
      }

      default:
        throw new Error(unsupportedCardError(this.type));
    }
  }

  /**
   * @description Updates the date value for post and search post card types.
   */
  private setDate(): void {
    switch (this.type) {
      case CardType.POST:
        this.card.created = {
          date: (this.data as PostBasic).date,
          dateFormat: AppValues.DATE_FORMAT_LONG,
        };
        return;
      case CardType.COMMENT:
        this.card.created = {
          date: (this.data as CommentSearchView).comment_creation_date,
          dateFormat: AppValues.DATE_TIME_FORMAT_LONG,
        };
        return;
    }
  }

  private setSearchEntityCreator(): void {
    if (this.type == CardType.COMMENT) {
      const comment: CommentSearchView = this.data as CommentSearchView;
      this.card.entityCreator = {
        userId: comment.comment_sender.user_id,
        displayName: comment.comment_sender.displayed_name,
        accountStatus: comment.comment_sender.account_status,
      };
    }
  }

  private setUrlFragment(): void {
    if (this.type == CardType.COMMENT) {
      const comment: CommentSearchView = this.data as CommentSearchView;
      this.card.urlFragment = comment.comment_id;
    }
  }

  /**
   * @description Adds 'remove' button as actionBtn value for post card type
   *              in case the user has 'remove post' permission and 'invite'
   *              for invite additional type
   */
  private setActionButtonHint(): void {
    this.card.actionBtnHint = this.actionBtnHint;
  }

  private setActionBtnId(): void {
    this.card.actionBtnId = this.getCardId();
  }

  private setActionButtonStatus(): void {
    if (this.status === AccountStatus.DEACTIVATED && this.sessionService.isAdminSession()) {
      this.card.actionDisabled = false;
      return;
    }

    if (this.status === AccountStatus.DEACTIVATED && this.type !== CardType.COMMENT) {
      this.card.actionDisabled = true;
      return;
    }

    if (this.status === ItemStatus.DELETED) {
      this.card.actionDisabled = true;
      return;
    }

    this.card.actionDisabled = false;
  }

  private defineImage(data: CardModelType): string {
    return getImageUrl(data['creator'] ? (data as SocialInitiative).creator.image_url : (data as FullUser).image_url);
  }
}

export interface CardBuilderContext<TData> {
  data: TData;
  view: 'browse' | 'invite';
  isAdmin: boolean;
  isAuthenticated: boolean;
  isRequariedAuthentication: boolean;
}

export abstract class BaseCardBuilder<TData> {
  protected readonly HEADER_BADGE_STYLES: Record<EntityStatus, string> = {
    '': '',
    ACTIVE: '',
    BLOCKED: 'blocked-badge',
    DELETED: 'deleted-badge',
    PRIVATE: 'private-badge',
    DEACTIVATED: 'deactivated-badge',
    PROJECT_EXPIRED: 'expired-badge',
  };

  protected translate = (key: string, params?: { [key: string]: string }): string => {
    return this.translateService.instant(key, params) as string;
  };

  public constructor(protected translateService: TranslateService) {}

  public build(context: CardBuilderContext<TData>): UICard {
    return {
      id: this.id(context),
      link: this.link(context),
      title: this.title(context),
      button: this.button(context),
      avatar: this.avatar(context),
      content: this.content(context),
      subtitle: this.subtitle(context),
      headerBadge: this.headerBadge(context),
      contentBadge: this.contentBadge(context),
    };
  }

  public toLegacyCardModel(context: CardBuilderContext<TData>): CardModel {
    const uiCard = this.build(context);

    return {
      id: uiCard.id,
      header: uiCard.title,
      title: uiCard.subtitle,
      status: uiCard?.headerBadge?.text as EntityStatus,
      buttonHint: uiCard.link.text,
      buttonRouterLink: uiCard?.link?.href,
      buttonRouterParams: uiCard?.link?.queryParams,
      badge: uiCard?.contentBadge?.text as UserRelationToGroup,
      description: uiCard.content,
      descriptionType: CardInfoType.string,
      created: null,
      actionBtnHint: null,
      image: null,
      actionBtnId: uiCard.id,
      actionDisabled: !uiCard.link?.isActive,
    };
  }

  protected abstract id: (context: CardBuilderContext<TData>) => string;
  protected abstract title: (context: CardBuilderContext<TData>) => string;
  protected abstract subtitle: (context: CardBuilderContext<TData>) => string;
  protected abstract content: (context: CardBuilderContext<TData>) => string;
  protected abstract link: (context: CardBuilderContext<TData>) => MaybeNull<Link>;
  protected abstract button: (context: CardBuilderContext<TData>) => MaybeNull<Button>;
  protected abstract avatar: (context: CardBuilderContext<TData>) => MaybeNull<Avatar>;
  protected abstract headerBadge: (context: CardBuilderContext<TData>) => MaybeNull<Badge>;
  protected abstract contentBadge: (context: CardBuilderContext<TData>) => MaybeNull<Badge>;
}

export class CommunityCardBuilder extends BaseCardBuilder<CommunityBasic> {
  protected id = ({ data }: CardBuilderContext<CommunityBasic>): string => {
    return data.entity_id;
  };

  protected title = (_context: CardBuilderContext<CommunityBasic>): string => {
    return this.translate('COMMUNITY_TITLE');
  };

  protected subtitle = ({ data }: CardBuilderContext<CommunityBasic>): string => {
    return data.title;
  };

  protected content = ({ data }: CardBuilderContext<CommunityBasic>): string => {
    return formatCategoryList(data.key_categories);
  };

  protected headerBadge = ({ data }: CardBuilderContext<CommunityBasic>): MaybeNull<Badge> => {
    const status = data.status === CommunityStatus.Closed ? ItemStatus.PRIVATE : ItemStatus.EMPTY;

    return status !== ItemStatus.EMPTY ? { text: status, style: this.HEADER_BADGE_STYLES[status] } : null;
  };

  protected contentBadge = ({ data }: CardBuilderContext<CommunityBasic>): MaybeNull<Badge> => {
    const role = data.actor_role !== UserRelationToGroup.Unrelated ? data.actor_role : '';

    return role ? { text: role, style: '' } : null;
  };

  protected link = ({ data }: CardBuilderContext<CommunityBasic>): Link => ({
    isActive: true,
    text: 'BTN_VISIT_COMMUNITY',
    href: entityBasedCommunityLink(data),
  });

  protected button = (_context: CardBuilderContext<CommunityBasic>) => null;
  protected avatar = (_context: CardBuilderContext<CommunityBasic>) => null;
}

export class ProjectCardBuilder extends BaseCardBuilder<ProjectBasic> {
  protected id = ({ data }: CardBuilderContext<ProjectBasic>): string => {
    return data.entity_id;
  };
  protected title = (_context: CardBuilderContext<ProjectBasic>): string => {
    return this.translate('PROJECT_TITLE');
  };

  protected subtitle = ({ data }: CardBuilderContext<ProjectBasic>): string => {
    return data.title;
  };

  protected content = ({ data }: CardBuilderContext<ProjectBasic>): string => {
    return formatCategoryList(data.specialities);
  };

  protected headerBadge = ({ data }: CardBuilderContext<ProjectBasic>): MaybeNull<Badge> => {
    const isCreatorActive = data.creator.account_status === AccountStatus.ACTIVE;
    const isProjectExpired = isDateLaterNow(data.validity_period.end_date);

    if (!isCreatorActive) {
      const status = getStatus(data.creator);

      return { text: status, style: this.HEADER_BADGE_STYLES[status] };
    }

    if (isCreatorActive && isProjectExpired) {
      return { text: ItemStatus.EXPIRED, style: this.HEADER_BADGE_STYLES[ItemStatus.EXPIRED] };
    }

    return null;
  };

  protected contentBadge = ({ data }: CardBuilderContext<ProjectBasic>): MaybeNull<Badge> => {
    const role = data.actor_role !== UserRelationToGroup.Unrelated ? data.actor_role : '';

    return role ? { text: role, style: '' } : null;
  };

  protected link = ({ data }: CardBuilderContext<ProjectBasic>): Link => {
    return {
      isActive: data.creator.account_status !== AccountStatus.DEACTIVATED,
      text: 'BTN_VIEW_PROJECT',
      href: `${ProjectLinks.LINK_PROJECTS_VIEW}/${data.entity_id}`,
    };
  };

  protected button = (_context: CardBuilderContext<ProjectBasic>) => null;
  protected avatar = (_context: CardBuilderContext<ProjectBasic>) => null;
}
